import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import {
  Card,
  Tabs,
  Table,
  Tag,
  Badge,
  Spin,
  Empty,
  Input,
  Button,
  Space,
  Row,
  Col,
  Tooltip,
  Typography,
} from "antd";
import {
  PrinterOutlined,
  SendOutlined,
  ReloadOutlined,
  SearchOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { getPrintableInvoices, getDispatchOrders } from "../../../actions/report";
import PrintInvoiceItem from "../../Order/PrintInvoice/PrintInvoiceItem";
import UpdateDispatchDrawer from "../../Order/DispatchOrder/UpdateDispatchDrawer";

const { TabPane } = Tabs;
const { Text } = Typography;

const InvoicePrintAndDispatch = ({
  location,
  reload,
  outletlist = [],
  getPrintableInvoices,
  getDispatchOrders,
  Auth,
}) => {
  const [activeTab, setActiveTab] = useState("printable");

  // Determine current location name/id
  // Allowed locations: "Main Warehouse" and "Ecommerce"
  const ALLOWED_LOCATION_NAMES = ["Main Warehouse", "Ecommerce"];

  const getCurrentLocationInfo = () => {
    const locValue = location && location.current !== undefined ? location.current : "";
    if (!locValue) {
      // If "All" or empty, return null
      return null;
    }
    // Check if locValue matches an outlet in outletlist
    const matchedOutlet = outletlist.find(
      (o) => String(o.id) === String(locValue) || String(o.name).trim().toLowerCase() === String(locValue).trim().toLowerCase()
    );
    if (matchedOutlet) {
      return matchedOutlet;
    }
    // Check Auth branch
    if (Auth && Auth.profile && Auth.profile.branch) {
      const branch = Auth.profile.branch;
      if (String(branch.id) === String(locValue) || String(branch.name).trim().toLowerCase() === String(locValue).trim().toLowerCase()) {
        return branch;
      }
    }
    return { id: locValue, name: String(locValue) };
  };

  const currentOutlet = getCurrentLocationInfo();
  const currentLocName = currentOutlet ? currentOutlet.name : "";
  const isAllowedLocation =
    currentLocName &&
    ALLOWED_LOCATION_NAMES.some(
      (allowed) => allowed.toLowerCase() === currentLocName.trim().toLowerCase()
    );

  // Printable Invoices state
  const [printableData, setPrintableData] = useState([]);
  const [printableLoading, setPrintableLoading] = useState(false);
  const [printableTotal, setPrintableTotal] = useState(0);
  const [printablePage, setPrintablePage] = useState(1);
  const [printablePageSize, setPrintablePageSize] = useState(10);
  const [printableSearch, setPrintableSearch] = useState("");

  // Dispatch Invoices state (is_printable=false, dispatch_order=false)
  const [dispatchData, setDispatchData] = useState([]);
  const [dispatchLoading, setDispatchLoading] = useState(false);
  const [dispatchTotal, setDispatchTotal] = useState(0);
  const [dispatchPage, setDispatchPage] = useState(1);
  const [dispatchPageSize, setDispatchPageSize] = useState(10);
  const [dispatchSearch, setDispatchSearch] = useState("");

  // Drawer states
  const [printDrawerVisible, setPrintDrawerVisible] = useState(false);
  const [selectedPrintInvoice, setSelectedPrintInvoice] = useState(null);

  const [dispatchDrawerVisible, setDispatchDrawerVisible] = useState(false);
  const [selectedDispatchInvoice, setSelectedDispatchInvoice] = useState(null);

  // Search timers
  const printableTimer = useRef(null);
  const dispatchTimer = useRef(null);

  const fetchPrintableInvoices = async (
    page = printablePage,
    pageSize = printablePageSize,
    keyword = printableSearch
  ) => {
    if (!isAllowedLocation) {
      setPrintableData([]);
      setPrintableTotal(0);
      return;
    }
    setPrintableLoading(true);
    try {
      const locId = currentOutlet ? currentOutlet.id : "";
      const result = await getPrintableInvoices(
        "", // start
        "", // end
        keyword,
        "", // invoice_number
        "", // delivery_date
        "", // Payment_method
        "", // status
        "", // contact
        locId,
        "", // account
        page,
        pageSize
      );
      if (result) {
        const filtered = (result.results || []).filter((inv) => inv.status !== "Cancelled");
        setPrintableData(filtered);
        setPrintableTotal(result.count || 0);
      } else {
        setPrintableData([]);
        setPrintableTotal(0);
      }
    } catch (err) {
      console.error("Error fetching printable invoices:", err);
      setPrintableData([]);
      setPrintableTotal(0);
    } finally {
      setPrintableLoading(false);
    }
  };

  const fetchDispatchInvoices = async (
    page = dispatchPage,
    pageSize = dispatchPageSize,
    keyword = dispatchSearch
  ) => {
    if (!isAllowedLocation) {
      setDispatchData([]);
      setDispatchTotal(0);
      return;
    }
    setDispatchLoading(true);
    try {
      const locId = currentOutlet ? currentOutlet.id : "";
      const result = await getDispatchOrders(
        "", // start
        "", // end
        keyword,
        "", // invoice_number
        "", // delivery_date
        "", // Payment_method
        "", // status
        "", // contact
        locId,
        "", // account
        page,
        pageSize
      );
      if (result) {
        const filtered = (result.results || []).filter((inv) => inv.status !== "Cancelled");
        setDispatchData(filtered);
        setDispatchTotal(result.count || 0);
      } else {
        setDispatchData([]);
        setDispatchTotal(0);
      }

    } catch (err) {
      console.error("Error fetching dispatch invoices:", err);
      setDispatchData([]);
      setDispatchTotal(0);
    } finally {
      setDispatchLoading(false);
    }
  };

  useEffect(() => {
    if (isAllowedLocation) {
      fetchPrintableInvoices(1, printablePageSize, printableSearch);
      setPrintablePage(1);
      fetchDispatchInvoices(1, dispatchPageSize, dispatchSearch);
      setDispatchPage(1);
    } else {
      setPrintableData([]);
      setPrintableTotal(0);
      setDispatchData([]);
      setDispatchTotal(0);
    }
  }, [location && location.current, outletlist, reload]);


  const handlePrintableSearchChange = (e) => {
    const val = e.target.value;
    setPrintableSearch(val);
    clearTimeout(printableTimer.current);
    printableTimer.current = setTimeout(() => {
      setPrintablePage(1);
      fetchPrintableInvoices(1, printablePageSize, val);
    }, 400);
  };

  const handleDispatchSearchChange = (e) => {
    const val = e.target.value;
    setDispatchSearch(val);
    clearTimeout(dispatchTimer.current);
    dispatchTimer.current = setTimeout(() => {
      setDispatchPage(1);
      fetchDispatchInvoices(1, dispatchPageSize, val);
    }, 400);
  };

  const printableColumns = [
    {
      title: "SL",
      key: "sl",
      width: 50,
      align: "center",
      render: (_, __, index) => (printablePage - 1) * printablePageSize + index + 1,
    },
    {
      title: "Invoice Number",
      dataIndex: "invoice_number",
      key: "invoice_number",
      render: (text) => <Text strong>{text || "-"}</Text>,
    },
    {
      title: "Order Number",
      dataIndex: "order_number",
      key: "order_number",
      render: (text) => text || "-",
    },
    {
      title: "Location",
      dataIndex: "Location_name",
      key: "Location_name",
      render: (text) => text || "-",
    },
    {
      title: "Contact Phone",
      dataIndex: "Contact",
      key: "contact_phone",
      render: (contact, record) => {
        if (contact && contact.length > 0 && contact[0].phone) {
          return contact[0].phone;
        }
        if (record.shipping_phone) {
          return record.shipping_phone;
        }
        return "-";
      },
    },
    {
      title: "Issued Date",
      dataIndex: "issue_date",
      key: "issue_date",
      render: (date) => date || "-",
    },
    {
      title: "Total Bill",
      dataIndex: "bill",
      key: "bill",
      align: "right",
      render: (bill) => (bill ? `${bill} ৳` : "0 ৳"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={status === "Delivered" ? "green" : "blue"}>{status || "N/A"}</Tag>
      ),
    },
    // {
    //   title: "Action",
    //   key: "action",
    //   align: "center",
    //   render: (_, record) => (
    //     <Button
    //       type="primary"
    //       size="small"
    //       icon={<PrinterOutlined />}
    //       onClick={() => {
    //         setSelectedPrintInvoice(record);
    //         setPrintDrawerVisible(true);
    //       }}
    //     >
    //       Print Invoice
    //     </Button>
    //   ),
    // },
  ];

  const dispatchColumns = [
    {
      title: "SL",
      key: "sl",
      width: 50,
      align: "center",
      render: (_, __, index) => (dispatchPage - 1) * dispatchPageSize + index + 1,
    },
    {
      title: "Invoice Number",
      dataIndex: "invoice_number",
      key: "invoice_number",
      render: (text) => <Text strong>{text || "-"}</Text>,
    },
    {
      title: "Order Number",
      dataIndex: "order_number",
      key: "order_number",
      render: (text) => text || "-",
    },
    {
      title: "Location",
      dataIndex: "Location_name",
      key: "Location_name",
      render: (text) => text || "-",
    },
    {
      title: "Contact Phone",
      dataIndex: "Contact",
      key: "contact_phone",
      render: (contact, record) => {
        if (contact && contact.length > 0 && contact[0].phone) {
          return contact[0].phone;
        }
        if (record.shipping_phone) {
          return record.shipping_phone;
        }
        return "-";
      },
    },
    {
      title: "Issued Date",
      dataIndex: "issue_date",
      key: "issue_date",
      render: (date) => date || "-",
    },
    {
      title: "Total Bill",
      dataIndex: "bill",
      key: "bill",
      align: "right",
      render: (bill) => (bill ? `${bill} ৳` : "0 ৳"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={status === "Delivered" ? "green" : "gold"}>{status || "N/A"}</Tag>
      ),
    },
    // {
    //   title: "Action",
    //   key: "action",
    //   align: "center",
    //   render: (_, record) => (
    //     <Button
    //       type="primary"
    //       size="small"
    //       style={{ backgroundColor: "#fa8c16", borderColor: "#fa8c16" }}
    //       icon={<SendOutlined />}
    //       onClick={() => {
    //         setSelectedDispatchInvoice(record);
    //         setDispatchDrawerVisible(true);
    //       }}
    //     >
    //       Dispatch Order
    //     </Button>
    //   ),
    // },
  ];

  if (!isAllowedLocation) {
    return null;
  }

  return (
    <div style={{ marginTop: "1rem", marginBottom: "1rem" }}>
      <Card

        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "8px",
            }}
          >
            <Space align="center">
              <PrinterOutlined style={{ fontSize: "18px", color: "#1890ff" }} />
              <span style={{ fontSize: "16px", fontWeight: "bold" }}>
                Invoice Print & Dispatch List
              </span>
            </Space>

            <Button
              type="text"
              icon={<ReloadOutlined spin={printableLoading || dispatchLoading} />}
              onClick={() => {
                fetchPrintableInvoices(printablePage, printablePageSize, printableSearch);
                fetchDispatchInvoices(dispatchPage, dispatchPageSize, dispatchSearch);
              }}
              size="small"
            >
              Refresh
            </Button>
          </div>
        }
        style={{
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
          borderRadius: "8px",
        }}
        bodyStyle={{ padding: "16px" }}
      >
        <Tabs activeKey={activeTab} onChange={(key) => setActiveTab(key)} type="card">
          <TabPane
            tab={
              <span>
                <PrinterOutlined style={{ marginRight: 6 }} />
                Printable Invoices{" "}
                <Badge
                  count={printableTotal}
                  overflowCount={9999}
                  style={{ backgroundColor: "#1890ff", marginLeft: 6 }}
                />
              </span>
            }
            key="printable"
          >
            <Row style={{ marginBottom: 16 }}>
              <Col span={8}>
                <Input
                  prefix={<SearchOutlined />}
                  placeholder="Search invoice number, phone, status..."
                  value={printableSearch}
                  onChange={handlePrintableSearchChange}
                  allowClear
                  size="small"
                />
              </Col>
            </Row>

            <Spin spinning={printableLoading}>
              <Table
                dataSource={printableData}
                columns={printableColumns}
                rowKey="id"
                size="small"
                bordered
                pagination={{
                  current: printablePage,
                  pageSize: printablePageSize,
                  total: printableTotal,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50", "100"],
                  onChange: (page, pageSize) => {
                    setPrintablePage(page);
                    setPrintablePageSize(pageSize);
                    fetchPrintableInvoices(page, pageSize, printableSearch);
                  },
                }}
              />
            </Spin>
          </TabPane>

          <TabPane
            tab={
              <span>
                <SendOutlined style={{ marginRight: 6 }} />
                Dispatch Invoices{" "}
                <Badge
                  count={dispatchTotal}
                  overflowCount={9999}
                  style={{ backgroundColor: "#fa8c16", marginLeft: 6 }}
                />
              </span>
            }
            key="dispatch"
          >
            <Row style={{ marginBottom: 16 }}>
              <Col span={8}>
                <Input
                  prefix={<SearchOutlined />}
                  placeholder="Search invoice number, phone, status..."
                  value={dispatchSearch}
                  onChange={handleDispatchSearchChange}
                  allowClear
                  size="small"
                />
              </Col>
            </Row>

            <Spin spinning={dispatchLoading}>
              <Table
                dataSource={dispatchData}
                columns={dispatchColumns}
                rowKey="id"
                size="small"
                bordered
                pagination={{
                  current: dispatchPage,
                  pageSize: dispatchPageSize,
                  total: dispatchTotal,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50", "100"],
                  onChange: (page, pageSize) => {
                    setDispatchPage(page);
                    setDispatchPageSize(pageSize);
                    fetchDispatchInvoices(page, pageSize, dispatchSearch);
                  },
                }}
              />
            </Spin>
          </TabPane>
        </Tabs>
      </Card>

      {/* Print Invoice Items Drawer */}
      <PrintInvoiceItem
        visible={printDrawerVisible}
        onClose={() => {
          setPrintDrawerVisible(false);
          setSelectedPrintInvoice(null);
        }}
        invoice={selectedPrintInvoice}
        onReload={() => {
          fetchPrintableInvoices(printablePage, printablePageSize, printableSearch);
          fetchDispatchInvoices(dispatchPage, dispatchPageSize, dispatchSearch);
        }}
      />

      {/* Dispatch Order Drawer */}
      <UpdateDispatchDrawer
        visible={dispatchDrawerVisible}
        onClose={() => {
          setDispatchDrawerVisible(false);
          setSelectedDispatchInvoice(null);
        }}
        invoice={selectedDispatchInvoice}
        onReload={() => {
          fetchPrintableInvoices(printablePage, printablePageSize, printableSearch);
          fetchDispatchInvoices(dispatchPage, dispatchPageSize, dispatchSearch);
        }}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  Auth: state.auth,
});

export default connect(mapStateToProps, {
  getPrintableInvoices,
  getDispatchOrders,
})(InvoicePrintAndDispatch);
